import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { Badge, Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import Loader from '../../../common/Loader';
import { PEN_SYMBOL } from '../../../../config/constants';
import { DATE_TIME_FORMAT, TIME_ZONE } from '../../../../config/locale';
import { numberFormatter } from '../../../../utils/number';
import {
  FuelBarChart,
  FuelLineChart,
  FuelStatusDonut,
} from './FuelDailyCharts';
import './FuelDailyOperationalDashboardResult.css';

const STATUS_BADGE = {
  CALCULATED: 'success',
  IN_PROGRESS: 'primary',
  PENDING_LIQUIDATION: 'info',
  OVERDUE: 'danger',
  MISSING_REFILL: 'warning',
  OBSERVED: 'secondary',
};

const NORMAL_PENDING_STATUSES = [
  'PENDING_LIQUIDATION',
  'IN_PROGRESS',
  'MISSING_REFILL',
  'OBSERVED',
];

const fmtNum = (value, fractionDigits = 0) => {
  if (value == null || value === '') {
    return '—';
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '—';
  }
  return numberFormatter({
    style: 'decimal',
    value: n,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

const fmtMoney = (value) => {
  if (value == null || value === '') {
    return '—';
  }
  return `${PEN_SYMBOL} ${fmtNum(value, 0)}`;
};

const fmtDateTime = (epoch) => {
  if (epoch == null || epoch === '') {
    return '—';
  }
  const m = moment(Number(epoch)).tz(TIME_ZONE);
  if (!m.isValid()) {
    return '—';
  }
  return m.format(DATE_TIME_FORMAT);
};

const rowKey = (r) =>
  r.circuitGroupId != null
    ? `g-${r.circuitGroupId}`
    : `c-${r.circuitId || ''}-${r.companyBusId || ''}-${r.status || ''}`;

const KpiCard = ({ title, value, unit, tone, onClick, hint }) => {
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      className={`fuel-daily-ops-kpi${clickable ? ' is-clickable' : ''}`}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      title={hint || (clickable ? 'Ver detalle' : undefined)}
    >
      <h4>{title}</h4>
      <div className={`value ${tone || ''}`}>
        {value} {unit ? <span className="unit">{unit}</span> : null}
      </div>
      {clickable ? <span className="fuel-daily-ops-kpi-hint">Ver listado</span> : null}
    </button>
  );
};

KpiCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unit: PropTypes.string,
  tone: PropTypes.string,
  onClick: PropTypes.func,
  hint: PropTypes.string,
};

KpiCard.defaultProps = {
  unit: '',
  tone: '',
  onClick: null,
  hint: '',
};

const CircuitRowsTable = ({ rows, onRowClick, compact }) => (
  <div className="fuel-daily-ops-table-wrap">
    <table className="table table-sm table-bordered mb-0">
      <thead>
        <tr>
          <th>Circuito</th>
          <th>Unidad</th>
          <th title="Días desde la primera salida hasta la liquidación (o hasta hoy si aún no liquidó)">
            Días desde inicio
          </th>
          {!compact && <th>KM</th>}
          {!compact && <th>Real gal</th>}
          {!compact && <th>Esperado gal</th>}
          {!compact && <th>Desv %</th>}
          {!compact && <th>Impacto</th>}
          <th>Estado</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={compact ? 4 : 9} className="text-center text-muted">
              Sin circuitos en esta selección
            </td>
          </tr>
        )}
        {rows.map((r) => {
          const dev = Number(r.deviationPercent) || 0;
          return (
            <tr
              key={rowKey(r)}
              className="clickable"
              onClick={() => onRowClick(r)}
            >
              <td>
                <b>{r.circuitLabel}</b>
                {r.circuitId != null ? (
                  <div className="text-muted small">ID {r.circuitId}</div>
                ) : null}
              </td>
              <td>{r.companyBusId || '—'}</td>
              <td title="Días desde la primera salida hasta la liquidación (o hasta hoy si aún no liquidó)">
                {r.daysOpen != null ? r.daysOpen : '—'}
              </td>
              {!compact && <td>{fmtNum(r.kilometers)}</td>}
              {!compact && <td>{fmtNum(r.realGallons)}</td>}
              {!compact && <td>{fmtNum(r.expectedGallons)}</td>}
              {!compact && (
                <td className={dev > 0 ? 'text-danger' : 'text-success'}>
                  <b>
                    {dev > 0 ? '+' : ''}
                    {fmtNum(dev, 1)}%
                  </b>
                </td>
              )}
              {!compact && <td>{fmtMoney(r.economicImpact)}</td>}
              <td>
                <Badge color={STATUS_BADGE[r.status] || 'secondary'}>
                  {r.statusLabel || r.status}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

CircuitRowsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowClick: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

CircuitRowsTable.defaultProps = {
  compact: false,
};

export class FuelDailyOperationalDashboardResult extends Component {
  constructor(props) {
    super(props);
    this.state = {
      detail: null,
      listModal: null,
    };
  }

  openDetail = (row) => {
    this.setState({ detail: row });
  };

  closeDetail = () => {
    this.setState({ detail: null });
  };

  openList = (listModal) => {
    this.setState({ listModal });
  };

  closeList = () => {
    this.setState({ listModal: null });
  };

  openListRow = (row) => {
    this.setState({ detail: row });
  };

  filterRows = (rows, predicate) => rows.filter(predicate);

  render() {
    const { loading, report } = this.props;
    const { detail, listModal } = this.state;

    if (loading) {
      return <Loader />;
    }

    if (!report || report.isEmpty()) {
      return (
        <div className="mt-4 text-muted">
          Aplica filtros para cargar el dashboard operativo.
        </div>
      );
    }

    const kpis = report.get('kpis') ? report.get('kpis').toJS() : {};
    const pending = report.get('pendingLiquidation')
      ? report.get('pendingLiquidation').toJS()
      : {};
    const bars = report.get('realVsExpected')
      ? report.get('realVsExpected').toJS()
      : [];
    const trend = report.get('performanceTrend')
      ? report.get('performanceTrend').toJS()
      : [];
    const slices = report.get('liquidationStatus')
      ? report.get('liquidationStatus').toJS()
      : [];
    const rows = report.get('criticalCircuits')
      ? report.get('criticalCircuits').toJS()
      : [];

    const excess = Number(kpis.netExcessGallons) || 0;
    const excessTone = excess > 0 ? 'text-danger' : 'text-success';

    const byStatus = (status) =>
      this.filterRows(rows, (r) => r.status === status);
    const byStatuses = (statuses) =>
      this.filterRows(rows, (r) => statuses.includes(r.status));

    const detailItineraries =
      detail && Array.isArray(detail.itineraries) ? detail.itineraries : [];

    return (
      <div className="mt-4">
        <div className="fuel-daily-ops-kpis">
          <KpiCard
            title="Circuitos cerrados"
            value={fmtNum(kpis.closedCircuits)}
            onClick={() =>
              this.openList({
                title: 'Circuitos cerrados (calculados)',
                rows: byStatus('CALCULATED'),
              })
            }
          />
          <KpiCard
            title="Circuitos en curso"
            value={fmtNum(kpis.circuitsInProgress)}
            onClick={() =>
              this.openList({
                title: 'Circuitos en curso',
                rows: byStatus('IN_PROGRESS'),
              })
            }
          />
          <KpiCard
            title="Rendimiento real"
            value={fmtNum(kpis.realPerformanceKmPerGallon, 2)}
            unit="km/gal"
            tone="text-success"
          />
          <KpiCard
            title="Galones reales"
            value={fmtNum(kpis.realGallons)}
            unit="gal"
          />
          <KpiCard
            title="Galones esperados"
            value={fmtNum(kpis.expectedGallons)}
            unit="gal"
          />
          <KpiCard
            title="Exceso neto"
            value={fmtNum(kpis.netExcessGallons)}
            unit="gal"
            tone={excessTone}
          />
          <KpiCard
            title="Impacto económico"
            value={fmtMoney(kpis.economicImpact)}
            tone={excessTone}
          />
          <KpiCard
            title="Cobertura KM liquidados"
            value={`${fmtNum(kpis.liquidatedKmCoveragePercent, 1)}%`}
            tone="text-success"
          />
          <KpiCard
            title="Circuitos pendientes"
            value={fmtNum(kpis.finishedItinerariesPending)}
            tone="text-warning"
            hint="Circuitos aún no calculados"
            onClick={() =>
              this.openList({
                title: 'Circuitos con liquidación pendiente',
                rows: byStatuses([
                  'PENDING_LIQUIDATION',
                  'OVERDUE',
                  'IN_PROGRESS',
                  'MISSING_REFILL',
                  'OBSERVED',
                ]),
              })
            }
          />
          <KpiCard
            title="Sin relleno final"
            value={fmtNum(kpis.returnedWithoutFinalRefill)}
            tone="text-danger"
            onClick={() =>
              this.openList({
                title: 'Retornados sin relleno',
                rows: byStatus('MISSING_REFILL'),
              })
            }
          />
        </div>

        <div className="fuel-daily-ops-section-title">
          Operación pendiente de liquidación
        </div>
        <div className="fuel-daily-ops-pending">
          <button
            type="button"
            className="fuel-daily-ops-pending-card is-clickable"
            onClick={() =>
              this.openList({
                title: 'Pendientes normales',
                rows: byStatuses(NORMAL_PENDING_STATUSES),
              })
            }
          >
            <b>Pendientes normales</b>
            <div className="small-value">{fmtNum(pending.normalPending)}</div>
            <small className="text-muted">
              En curso, pendiente liquidar, sin relleno u observado
            </small>
          </button>
          <button
            type="button"
            className="fuel-daily-ops-pending-card is-clickable"
            onClick={() =>
              this.openList({
                title: 'Pendientes vencidos (≥ 3 días desde llegada a base)',
                rows: byStatus('OVERDUE'),
              })
            }
          >
            <b>Pendientes vencidos</b>
            <div className="small-value text-danger">
              {fmtNum(pending.overduePending)}
            </div>
            <small className="text-muted">
              Grupo CREATED con ≥ 3 días desde la llegada a base
            </small>
          </button>
          <div className="fuel-daily-ops-pending-card">
            <b>KM pendientes</b>
            <div className="small-value">
              {fmtNum(pending.pendingKilometers)}
            </div>
            <small className="text-muted">km sin liquidación final</small>
          </div>
          <div className="fuel-daily-ops-pending-card">
            <b>Dotación pendiente</b>
            <div className="small-value">
              {fmtNum(pending.pendingAllocationGallons)}
            </div>
            <small className="text-muted">gal esperados por contrastar</small>
          </div>
        </div>

        <div className="fuel-daily-ops-grid">
          <div className="fuel-daily-ops-panel">
            <h3>Real vs Esperado por circuito</h3>
            <FuelBarChart
              labels={bars.map((b) => b.label)}
              expected={bars.map((b) => Number(b.expectedGallons) || 0)}
              real={bars.map((b) => Number(b.realGallons) || 0)}
            />
          </div>
          <div className="fuel-daily-ops-panel">
            <h3>Tendencia de rendimiento (14 días)</h3>
            <FuelLineChart
              labels={trend.map((t) => t.label)}
              values={trend.map((t) => Number(t.kmPerGallon) || 0)}
            />
          </div>
          <div className="fuel-daily-ops-panel">
            <h3>Estado de liquidación</h3>
            <FuelStatusDonut
              slices={slices}
              onSliceClick={(key, label) =>
                this.openList({
                  title: `Estado: ${label || key}`,
                  rows: byStatus(key),
                })
              }
            />
          </div>
        </div>

        <div className="fuel-daily-ops-panel">
          <h3>Circuitos críticos y seguimiento</h3>
          <p className="fuel-daily-ops-table-help text-muted mb-2">
            Haz clic en una fila para ver rutas y fechas de sus itinerarios.
            <br />
            <b>Días desde inicio</b>: desde la primera salida hasta la
            liquidación (fecha de OC cuando existe); si aún no liquidó, hasta
            hoy. Los <b>vencidos</b> se miden desde la <b>llegada a base</b> (≥
            3 días).
          </p>
          <CircuitRowsTable rows={rows} onRowClick={this.openDetail} />
        </div>

        <div className="fuel-daily-ops-note">
          El consumo real se calcula por circuito cerrado tanque lleno → tanque
          lleno. Un itinerario terminado puede permanecer pendiente si su
          circuito aún sigue vigente.
        </div>

        <Modal isOpen={!!listModal} toggle={this.closeList} size="lg">
          <ModalHeader toggle={this.closeList}>
            {listModal ? listModal.title : 'Listado'}
          </ModalHeader>
          <ModalBody>
            {listModal && (
              <>
                <p className="text-muted small mb-2">
                  {listModal.rows.length} circuito(s). Haz clic en una fila
                  para ver itinerarios.
                </p>
                <CircuitRowsTable
                  rows={listModal.rows}
                  onRowClick={this.openListRow}
                  compact
                />
              </>
            )}
          </ModalBody>
        </Modal>

        <Modal isOpen={!!detail} toggle={this.closeDetail} size="lg">
          <ModalHeader toggle={this.closeDetail}>
            {detail
              ? `Detalle ${detail.circuitLabel} · ${detail.companyBusId || ''}`
              : 'Detalle'}
          </ModalHeader>
          <ModalBody>
            {detail && (
              <>
                <Row>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Circuito</b>
                    {detail.circuitLabel || '—'}
                    {detail.circuitId != null ? (
                      <div className="text-muted small">ID {detail.circuitId}</div>
                    ) : null}
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Grupo circuito</b>
                    {detail.circuitGroupId != null ? detail.circuitGroupId : '—'}
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Unidad</b>
                    {detail.companyBusId || '—'}
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Kilómetros</b>
                    {fmtNum(detail.kilometers)} km
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Consumo real</b>
                    {fmtNum(detail.realGallons)} gal
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Dotación esperada</b>
                    {fmtNum(detail.expectedGallons)} gal
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Desviación</b>
                    {fmtNum(detail.deviationPercent, 1)}%
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Impacto</b>
                    {fmtMoney(detail.economicImpact)}
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Estado</b>
                    <Badge color={STATUS_BADGE[detail.status] || 'secondary'}>
                      {detail.statusLabel || detail.status}
                    </Badge>
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">
                      Días desde inicio
                    </b>
                    {detail.daysOpen != null ? detail.daysOpen : '—'}
                    <div className="text-muted small">
                      1.ª salida → liquidación (o hoy si pendiente)
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Base</b>
                    {detail.homeBaseName || '—'}
                  </Col>
                  <Col md={4} className="mb-3">
                    <b className="d-block text-muted small">Itinerarios</b>
                    {detail.routeCount != null ? detail.routeCount : '—'}
                  </Col>
                </Row>

                <h5 className="mt-2 mb-2">
                  Rutas e itinerarios
                  {detail.circuitId != null
                    ? ` · Circuito ID ${detail.circuitId}`
                    : ''}
                </h5>
                <div className="fuel-daily-ops-table-wrap">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Itinerario</th>
                        <th>Ruta</th>
                        <th>Salida</th>
                        <th>Llegada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailItineraries.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted"
                          >
                            Sin itinerarios asociados
                          </td>
                        </tr>
                      )}
                      {detailItineraries.map((leg) => (
                        <tr key={leg.itineraryId || `${leg.routeId}-${leg.departureTime}`}>
                          <td>{leg.itineraryId || '—'}</td>
                          <td>{leg.routeName || '—'}</td>
                          <td>{fmtDateTime(leg.departureTime)}</td>
                          <td>{fmtDateTime(leg.arrivalTime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

FuelDailyOperationalDashboardResult.propTypes = {
  loading: PropTypes.bool,
  report: PropTypes.instanceOf(Immutable.Map),
};

FuelDailyOperationalDashboardResult.defaultProps = {
  loading: false,
  report: null,
};

const mapStateToProps = ({ TrafficUnit }) => ({
  loading: !TrafficUnit.FuelDailyOperationalDashboard.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  report: TrafficUnit.FuelDailyOperationalDashboard.getIn([
    'current',
    'content',
  ]),
});

export default connect(mapStateToProps)(FuelDailyOperationalDashboardResult);
